import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import { startSession } from '../../app/slices/sessionSlice';
import { addToast } from '../../app/slices/uiSlice';
import { normalizeToKm } from '../../lib/units';
import { DistanceUnit } from '../../types/models';
import LoadingSpinner from '../../components/LoadingSpinner';

interface StartSessionFormProps {
  onDone: () => void;
}

export default function StartSessionForm({ onDone }: StartSessionFormProps) {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isLoading } = useSelector((state: RootState) => state.sessions);

  // Read unit from profile (no toggle here)
  const unit: DistanceUnit = user?.distanceUnit ?? 'km';
  const [odo, setOdo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.uid) {
      dispatch(
        addToast({
          message: 'User not authenticated',
          type: 'error',
        })
      );
      return;
    }

    const odoKm = normalizeToKm(odo, unit);
    if (odoKm == null || odoKm < 0) {
      dispatch(
        addToast({
          message: 'Enter a valid odometer reading',
          type: 'error',
        })
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        startSession({
          userId: user.uid,
          startOdometer: odoKm, // Always store in km
        })
      );

      dispatch(
        addToast({
          message: 'Driving session started! Drive safely! 🚗',
          type: 'success',
        })
      );

      onDone();
    } catch {
      dispatch(
        addToast({
          message: 'Failed to start session',
          type: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {/* Unit display (read-only) */}
      <div className="flex items-center gap-2">
        <span className="text-xs rounded-md bg-muted px-2 py-0.5">Unit: {unit}</span>
        <a href="/profile" className="text-xs text-primary underline">
          Change in Profile
        </a>
      </div>

      <div className="grid gap-1">
        <Label>Current Odometer Reading ({unit}) *</Label>
        <Input
          type="text"
          inputMode="decimal"
          placeholder={unit === 'km' ? 'e.g., 125000.5' : 'e.g., 77625.3'}
          value={odo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOdo(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Check your vehicle's odometer for the exact reading.
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          onClick={onDone}
          disabled={isSubmitting || isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          disabled={isSubmitting || isLoading}
        >
          {isSubmitting || isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Starting...
            </>
          ) : (
            'Start Session'
          )}
        </button>
      </div>
    </form>
  );
}
